import React, { useCallback, useEffect } from 'react';

import OfflineModeBroadcastCompose from './OfflineModeBroadcastCompose';
import { SkeletonBlock, SkeletonRegion } from './Skeleton';
import styles from './OfflineMode.module.scss';
import { BROADCAST } from './copy';
import type {
  OfflineModeBroadcastSendInput,
  OfflineModeBroadcastSendResult,
} from 'redux-stores/offlineMode';
import { actions, thunk, useAppDispatch, useAppSelector } from 'redux-stores';

export type OfflineModeBroadcastProps = {
  organizationId: string;
  organizationName: string;
  onSent: (result: OfflineModeBroadcastSendResult) => void;
  onCancel: () => void;
};

export default function OfflineModeBroadcast({
  organizationId,
  organizationName,
  onSent,
  onCancel,
}: OfflineModeBroadcastProps) {
  const dispatch = useAppDispatch();
  const {
    broadcastPrepare,
    broadcastPrepareError,
    broadcastPrepareStatus,
    broadcastRefusal,
    broadcastSending,
  } = useAppSelector((state) => state.offlineMode);

  const load = useCallback(() => {
    thunk.fetchOfflineModeBroadcastPrepare(dispatch, organizationId);
  }, [dispatch, organizationId]);

  useEffect(() => {
    load();
    return () => {
      dispatch(actions.clearOfflineModeBroadcast());
    };
  }, [dispatch, load]);

  const onSend = useCallback(
    async (input: OfflineModeBroadcastSendInput) => {
      const result = await thunk.sendOfflineModeBroadcast(dispatch, organizationId, input);
      if (result) onSent(result);
    },
    [dispatch, onSent, organizationId]
  );

  if (broadcastPrepareStatus === 'idle' || broadcastPrepareStatus === 'loading') {
    return (
      <SkeletonRegion label={BROADCAST.gathering} testId="offlineModeBroadcastSkeleton">
        <SkeletonBlock height="1.25rem" width="60%" />
        <SkeletonBlock height="1rem" width="80%" />
        <SkeletonBlock height="8rem" />
      </SkeletonRegion>
    );
  }

  if (broadcastPrepareStatus === 'ready' && broadcastPrepare) {
    return (
      <OfflineModeBroadcastCompose
        onCancel={onCancel}
        onSend={onSend}
        organizationName={organizationName}
        prepare={broadcastPrepare}
        refusal={broadcastRefusal}
        sending={broadcastSending}
      />
    );
  }

  if (!broadcastPrepareError || broadcastPrepareError === 'unreachable') return null;

  const reason =
    broadcastRefusal?.code === 'roster_unavailable'
      ? BROADCAST.refusal.roster_unavailable()
      : BROADCAST.loadFailure.reasons[broadcastPrepareError];

  return (
    <div className={styles.writeError} data-test-id="offlineModeBroadcastLoadError" role="alert">
      <span className={styles.writeErrorHeading}>{BROADCAST.loadFailure.heading}</span>
      <span className={styles.writeErrorBody}>{reason}</span>
      <div className={styles.writeErrorActions}>
        <button
          className={styles.secondaryAction}
          data-test-id="offlineModeBroadcastLoadRetry"
          onClick={load}
          type="button"
        >
          {BROADCAST.loadFailure.retryLabel}
        </button>
        <button
          className={styles.tertiaryAction}
          data-test-id="offlineModeBroadcastLoadDismiss"
          onClick={onCancel}
          type="button"
        >
          {BROADCAST.loadFailure.dismissLabel}
        </button>
      </div>
    </div>
  );
}
